import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useThree, useFrame } from '@react-three/fiber';
import { TransformControls as DreiTransformControls } from '@react-three/drei';
import * as THREE from 'three';

export default function TransformControls({ 
  selectedObject, 
  mode, 
  onTransformChange,
  onTransformStart,
  onTransformEnd,
  isSnappingEnabled = false,
  constraints = { x: true, y: true, z: true },
  scaleLimits = { min: 0.1, max: 3.0 },
  positionLimits = { x: { min: -5, max: 5 }, y: { min: -3, max: 5 }, z: { min: -5, max: 5 } },
  orbitControlsRef
}) {
  const { camera, gl, scene } = useThree();
  const controlsRef = useRef();
  const [isDragging, setIsDragging] = useState(false);
  const [previousOrbitEnabled, setPreviousOrbitEnabled] = useState(true);
  const [previousOrbitTarget, setPreviousOrbitTarget] = useState(null);

  // CRITICAL: Ensure the selected object has matrixAutoUpdate enabled
  useEffect(() => {
    if (selectedObject) {
      // Ensure the wrapper group has matrixAutoUpdate enabled
      if (selectedObject.matrixAutoUpdate !== undefined) {
        selectedObject.matrixAutoUpdate = true;
        console.log(`[TransformControls] Set matrixAutoUpdate = true for ${selectedObject.userData?.modelId}`);
      }
      
      // Also ensure all child meshes have matrixAutoUpdate enabled
      selectedObject.traverse((child) => {
        if (child.matrixAutoUpdate !== undefined) {
          child.matrixAutoUpdate = true;
        }
      });
    }
    
    // Cleanup function to detach controls when component unmounts or object changes
    return () => {
      if (controlsRef.current) {
        console.log(`[TransformControls] Cleaning up - detaching controls`);
        controlsRef.current.detach();
      }
    };
  }, [selectedObject]);

  // Handle transform start - disable orbit controls and store previous state
  const handleTransformStart = useCallback(() => {
    console.log(`[TransformControls] Transform started - disabling orbit controls`);
    setIsDragging(true);
    
    if (orbitControlsRef?.current) {
      // Store previous orbit state
      setPreviousOrbitEnabled(orbitControlsRef.current.enabled);
      if (orbitControlsRef.current.target) {
        setPreviousOrbitTarget(orbitControlsRef.current.target.clone());
      }
      
      // Disable orbit controls to prevent camera movement
      orbitControlsRef.current.enabled = false;
      console.log(`[TransformControls] Orbit controls disabled, previous state stored`);
    } else {
      console.warn(`[TransformControls] No OrbitControls ref provided!`);
    }
    
    // CRITICAL: Store camera state at the start of drag
    if (camera && selectedObject) {
      selectedObject.userData.lastCameraPosition = camera.position.clone();
      selectedObject.userData.lastCameraRotation = camera.rotation.clone();
      console.log(`[TransformControls] Camera state stored at drag start:`, {
        position: selectedObject.userData.lastCameraPosition,
        rotation: selectedObject.userData.lastCameraRotation
      });
    }
    
    if (onTransformStart) {
      onTransformStart();
    }
  }, [onTransformStart, camera, selectedObject, orbitControlsRef]);

  // Handle transform end - re-enable orbit controls and restore previous state
  const handleTransformEnd = useCallback(() => {
    console.log(`[TransformControls] Transform ended - re-enabling orbit controls`);
    setIsDragging(false);
    
    if (orbitControlsRef?.current) {
      // Re-enable orbit controls
      orbitControlsRef.current.enabled = previousOrbitEnabled;
      
      // Restore previous orbit target if available
      if (previousOrbitTarget) {
        orbitControlsRef.current.target.copy(previousOrbitTarget);
      }
      
      console.log(`[TransformControls] Orbit controls re-enabled, previous state restored`);
    }
    
    if (onTransformEnd) {
      onTransformEnd();
    }
  }, [onTransformEnd, previousOrbitEnabled, previousOrbitTarget, orbitControlsRef]);

  // Handle transform changes with debouncing for better performance
  const handleTransformChange = useCallback(() => {
    if (onTransformChange && selectedObject) {
      // Debounce transform changes to reduce performance impact
      if (selectedObject.userData.transformTimeout) {
        clearTimeout(selectedObject.userData.transformTimeout);
      }
      
      selectedObject.userData.transformTimeout = setTimeout(() => {
        // CRITICAL: Get the current transform from the object itself
        let position = selectedObject.position.clone();
        const rotation = selectedObject.rotation.clone();
        let scale = selectedObject.scale.clone();
        let scaleLimited = false;
        let positionLimited = false;
        
        // Apply position limits to prevent models from being moved too far
        if (mode === 'translate') {
          const originalPosition = position.clone();
          
          // Calculate the actual bottom of the model using bounding box
          const boundingBox = new THREE.Box3();
          boundingBox.setFromObject(selectedObject);
          const modelHeight = boundingBox.max.y - boundingBox.min.y;
          
          // Adjust Y position limit to account for model height
          // The grid level (y=0) should be where the bottom of the model touches
          const adjustedYMin = positionLimits.y.min + (modelHeight / 2);
          
          // More aggressive position clamping
          position.x = Math.max(positionLimits.x.min, Math.min(positionLimits.x.max, position.x));
          position.y = Math.max(adjustedYMin, Math.min(positionLimits.y.max, position.y));
          position.z = Math.max(positionLimits.z.min, Math.min(positionLimits.z.max, position.z));
          
          // Check if position was limited
          if (originalPosition.x !== position.x || originalPosition.y !== position.y || originalPosition.z !== position.z) {
            positionLimited = true;
            console.log(`[TransformControls] Position limited in onChange:`, {
              original: originalPosition,
              clamped: position,
              limits: positionLimits,
              modelHeight: modelHeight,
              adjustedYMin: adjustedYMin
            });
          }
          
          // Apply the clamped position back to the object immediately
          selectedObject.position.copy(position);
          selectedObject.updateMatrix();
          selectedObject.updateMatrixWorld(true);
          
          // Show a brief visual feedback when position limits are reached
          if (positionLimited && !selectedObject.userData.positionLimitNotified) {
            selectedObject.userData.positionLimitNotified = true;
            
            // Reset the notification flag after a short delay
            setTimeout(() => {
              if (selectedObject.userData) {
                selectedObject.userData.positionLimitNotified = false;
              }
            }, 1000);
            
            console.log(`[TransformControls] Position limit reached: X(${positionLimits.x.min}-${positionLimits.x.max}), Y(${adjustedYMin}-${positionLimits.y.max}), Z(${positionLimits.z.min}-${positionLimits.z.max})`);
          }
        }
        
        // Apply scale limits to prevent models from being too large or too small
        if (mode === 'scale') {
          const originalScale = scale.clone();
          
          scale.x = Math.max(scaleLimits.min, Math.min(scaleLimits.max, scale.x));
          scale.y = Math.max(scaleLimits.min, Math.min(scaleLimits.max, scale.y));
          scale.z = Math.max(scaleLimits.min, Math.min(scaleLimits.max, scale.z));
          
          // Check if scale was limited
          if (originalScale.x !== scale.x || originalScale.y !== scale.y || originalScale.z !== scale.z) {
            scaleLimited = true;
          }
          
          // Apply the clamped scale back to the object
          selectedObject.scale.copy(scale);
          selectedObject.updateMatrix();
          selectedObject.updateMatrixWorld(true);
          
          // Show a brief visual feedback when scale limits are reached
          if (scaleLimited && !selectedObject.userData.scaleLimitNotified) {
            selectedObject.userData.scaleLimitNotified = true;
            
            // Reset the notification flag after a short delay
            setTimeout(() => {
              if (selectedObject.userData) {
                selectedObject.userData.scaleLimitNotified = false;
              }
            }, 1000);
            
            // You could emit a custom event here to show a notification
            console.log(`[TransformControls] Scale limit reached: ${scaleLimits.min} - ${scaleLimits.max}`);
          }
        }
        
        onTransformChange({
          position: [position.x, position.y, position.z],
          rotation: [rotation.x, rotation.y, rotation.z],
          scale: [scale.x, scale.y, scale.z],
          scaleLimited,
          positionLimited
        });
      }, 16); // 60fps debouncing
    }
  }, [onTransformChange, selectedObject, mode, scaleLimits, positionLimits]);

  // Update transform controls when mode changes
  useEffect(() => {
    if (controlsRef.current) {
      controlsRef.current.setMode(mode);
      console.log(`[TransformControls] Mode changed to: ${mode}`);
    }
  }, [mode]);

  // Update transform controls when selected object changes
  useEffect(() => {
    if (controlsRef.current && selectedObject) {
      console.log(`[TransformControls] Attaching to object:`, selectedObject.userData?.modelId);
      console.log(`[TransformControls] Position limits received:`, positionLimits);
      
      // Ensure the object's matrix is up to date before attaching
      selectedObject.updateMatrix();
      selectedObject.updateMatrixWorld(true);
      
      controlsRef.current.attach(selectedObject);
      
      // Force controls to update their position
      if (controlsRef.current.updateMatrixWorld) {
        controlsRef.current.updateMatrixWorld(true);
      }
    } else if (controlsRef.current && !selectedObject) {
      // CRITICAL: Detach controls when no object is selected
      console.log(`[TransformControls] Detaching controls - no object selected`);
      controlsRef.current.detach();
    }
    
    // Cleanup function to clear timeout when object changes
    return () => {
      if (selectedObject && selectedObject.userData.transformTimeout) {
        clearTimeout(selectedObject.userData.transformTimeout);
        selectedObject.userData.transformTimeout = null;
      }
    };
  }, [selectedObject, positionLimits]);

  // Configure snapping based on mode and enabled state
  const getSnappingConfig = () => {
    if (!isSnappingEnabled) return {};
    
    switch (mode) {
      case 'translate':
        return { translationSnap: 0.1 };
      case 'rotate':
        return { rotationSnap: Math.PI / 18 }; // 10 degrees
      case 'scale':
        return { scaleSnap: 0.1 };
      default:
        return {};
    }
  };

  // Configure axis constraints
  const getAxisConfig = () => {
    if (!constraints.x || !constraints.y || !constraints.z) {
      return { axis: 'XYZ' };
    }
    return {};
  };

  // CRITICAL: Add protection against transform interference and camera movement
  useFrame(() => {
    if (isDragging && selectedObject) {
      // While dragging, ensure no other code modifies the transforms
      // This prevents the "snap back" issue
      if (selectedObject.userData?.transformProtected !== true) {
        selectedObject.userData.transformProtected = true;
      }
      
        // CRITICAL: Continuously enforce position limits during dragging
  if (mode === 'translate') {
    const currentPosition = selectedObject.position;
    let positionChanged = false;
    
    // Debug: Log current position and limits
    if (!selectedObject.userData.limitDebugLogged) {
      console.log(`[TransformControls] Position limits:`, positionLimits);
      console.log(`[TransformControls] Current position:`, currentPosition);
      selectedObject.userData.limitDebugLogged = true;
    }
    
    // Calculate the actual bottom of the model using bounding box
    const boundingBox = new THREE.Box3();
    boundingBox.setFromObject(selectedObject);
    const modelHeight = boundingBox.max.y - boundingBox.min.y;
    const modelBottom = currentPosition.y - (modelHeight / 2);
    
    // Adjust Y position limit to account for model height
    // The grid level (y=0) should be where the bottom of the model touches
    const adjustedYMin = positionLimits.y.min + (modelHeight / 2);
    
    // Clamp position to limits - more aggressive clamping
    const clampedX = Math.max(positionLimits.x.min, Math.min(positionLimits.x.max, currentPosition.x));
    const clampedY = Math.max(adjustedYMin, Math.min(positionLimits.y.max, currentPosition.y));
    const clampedZ = Math.max(positionLimits.z.min, Math.min(positionLimits.z.max, currentPosition.z));
    
    // Check if any position was clamped
    if (currentPosition.x !== clampedX || currentPosition.y !== clampedY || currentPosition.z !== clampedZ) {
      positionChanged = true;
      console.log(`[TransformControls] Position clamped:`, {
        original: { x: currentPosition.x, y: currentPosition.y, z: currentPosition.z },
        clamped: { x: clampedX, y: clampedY, z: clampedZ },
        modelHeight: modelHeight,
        modelBottom: modelBottom,
        adjustedYMin: adjustedYMin
      });
    }
    
    // Apply clamped values directly
    currentPosition.set(clampedX, clampedY, clampedZ);
    
    // Force update if position was clamped
    if (positionChanged) {
      selectedObject.updateMatrixWorld();
      // Also update the transform controls to reflect the clamped position
      if (controlsRef.current) {
        controlsRef.current.update();
      }
    }
  }
      
      // CRITICAL: Continuously enforce scale limits during dragging
      if (mode === 'scale') {
        const currentScale = selectedObject.scale;
        let scaleChanged = false;
        
        // Clamp scale to limits
        if (currentScale.x < scaleLimits.min) {
          currentScale.x = scaleLimits.min;
          scaleChanged = true;
        } else if (currentScale.x > scaleLimits.max) {
          currentScale.x = scaleLimits.max;
          scaleChanged = true;
        }
        
        if (currentScale.y < scaleLimits.min) {
          currentScale.y = scaleLimits.min;
          scaleChanged = true;
        } else if (currentScale.y > scaleLimits.max) {
          currentScale.y = scaleLimits.max;
          scaleChanged = true;
        }
        
        if (currentScale.z < scaleLimits.min) {
          currentScale.z = scaleLimits.min;
          scaleChanged = true;
        } else if (currentScale.z > scaleLimits.max) {
          currentScale.z = scaleLimits.max;
          scaleChanged = true;
        }
        
        // Force update if scale was clamped
        if (scaleChanged) {
          selectedObject.updateMatrixWorld();
        }
      }
      
      // CRITICAL: Prevent any camera movement during drag - but only check occasionally
      // Use frame counter to reduce performance impact
      if (!selectedObject.userData.frameCounter) {
        selectedObject.userData.frameCounter = 0;
      }
      selectedObject.userData.frameCounter++;
      
      // Only check camera state every 20 frames to reduce lag even more
      if (selectedObject.userData.frameCounter % 20 === 0) {
        // Use requestAnimationFrame to throttle updates for even smoother performance
        requestAnimationFrame(() => {
          // Check if orbit controls are still enabled
          if (orbitControlsRef.current && orbitControlsRef.current.enabled) {
            orbitControlsRef.current.enabled = false;
          }
          
          // Check camera position changes (much less aggressive)
          if (camera && selectedObject?.userData?.lastCameraPosition) {
            const cameraPosition = camera.position;
            const lastPosition = selectedObject.userData.lastCameraPosition;
            
            // Use direct comparison instead of cloning for better performance
            if (Math.abs(cameraPosition.x - lastPosition.x) > 0.001 ||
                Math.abs(cameraPosition.y - lastPosition.y) > 0.001 ||
                Math.abs(cameraPosition.z - lastPosition.z) > 0.001) {
              cameraPosition.copy(lastPosition);
              camera.updateMatrixWorld();
            }
          }
          
          // Check camera rotation changes (much less aggressive)
          if (camera && selectedObject?.userData?.lastCameraRotation) {
            const cameraRotation = camera.rotation;
            const lastRotation = selectedObject.userData.lastCameraRotation;
            
            // Use direct comparison instead of cloning for better performance
            if (Math.abs(cameraRotation.x - lastRotation.x) > 0.001 ||
                Math.abs(cameraRotation.y - lastRotation.y) > 0.001 ||
                Math.abs(cameraRotation.z - lastRotation.z) > 0.001) {
              cameraRotation.copy(lastRotation);
              camera.updateMatrixWorld();
            }
          }
        });
      }
    } else {
      // Reset frame counter when not dragging
      if (selectedObject) {
        selectedObject.userData.frameCounter = 0;
      }
    }
  });

  if (!selectedObject) {
    return null;
  }

  const snappingConfig = getSnappingConfig();
  const axisConfig = getAxisConfig();

  return (
    <DreiTransformControls
      ref={controlsRef}
      object={selectedObject}
      mode={mode}
      size={0.75}
      space="world"
      showX={constraints.x}
      showY={constraints.y}
      showZ={constraints.z}
      onMouseDown={handleTransformStart}
      onMouseUp={handleTransformEnd}
      onChange={handleTransformChange}
      // Touch support
      enablePan={true}
      enableRotate={true}
      enableScale={true}
      // CRITICAL: Prevent touch events from bubbling to orbit controls
      onTouchStart={(event) => {
        event.stopPropagation();
        handleTransformStart();
      }}
      onTouchEnd={(event) => {
        event.stopPropagation();
        handleTransformEnd();
      }}
      // Apply snapping and constraints
      {...snappingConfig}
      {...axisConfig}
    />
  );
}
